import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import QRCode from "qrcode";
import paynowLogo from "../assets/paynow-white-logo.png";

// Plain black-on-white square-module QR (no centre logo), inside a PayNow card.
const QR_OPTS = {
  errorCorrectionLevel: "M" as const,
  margin: 1,
  width: 512,
  color: { dark: "#000000", light: "#FFFFFF" },
};

const PAYNOW_PURPLE = "#8b2a8b";
// The logo source (440x220) has ~72px of transparent padding top/bottom around
// the wordmark — this is its true shape, matched to the CSS aspect-ratio.
const LOGO_ASPECT = 396 / 76;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Flattens the PayNow-styled card (purple card, white QR square, wordmark) into
 * one PNG — what gets downloaded / shared, matching what's shown on screen.
 */
async function renderCard(payload: string): Promise<HTMLCanvasElement> {
  const [qrImg, logoImg] = await Promise.all([
    QRCode.toDataURL(payload, QR_OPTS).then(loadImage),
    loadImage(paynowLogo),
  ]);

  const scale = 2; // export at 2x for a crisp download
  const W = 340 * scale;
  const pad = 20 * scale;
  const padBottom = 18 * scale;
  const gap = 16 * scale;
  const cardR = 20 * scale;
  const frameR = 14 * scale;
  const framePad = 14 * scale;

  const frameW = W - pad * 2;
  const qrSize = frameW - framePad * 2;
  const frameH = framePad * 2 + qrSize;
  const logoW = frameW * 0.46;
  const logoH = logoW / LOGO_ASPECT;
  const H = pad + frameH + gap + logoH + padBottom;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  roundedRectPath(ctx, 0, 0, W, H, cardR);
  ctx.fillStyle = PAYNOW_PURPLE;
  ctx.fill();

  const frameX = pad;
  const frameY = pad;
  roundedRectPath(ctx, frameX, frameY, frameW, frameH, frameR);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  ctx.drawImage(qrImg, frameX + framePad, frameY + framePad, qrSize, qrSize);

  // object-fit: cover, object-position: center — crop the source to the box shape.
  const iw = logoImg.naturalWidth;
  const ih = logoImg.naturalHeight;
  const coverScale = Math.max(logoW / iw, logoH / ih);
  const cropW = logoW / coverScale;
  const cropH = logoH / coverScale;
  const cropX = (iw - cropW) / 2;
  const cropY = (ih - cropH) / 2;
  const logoX = (W - logoW) / 2;
  const logoY = frameY + frameH + gap;
  ctx.drawImage(logoImg, cropX, cropY, cropW, cropH, logoX, logoY, logoW, logoH);

  return canvas;
}

/** iOS Safari's plain `<a download>` saves to Files, never Photos, and the OS share sheet's
 *  "Save Image" is the only route to Photos. Android's download manager already lands images
 *  where Gallery picks them up, so routing it through `navigator.share` only adds a chooser. */
function isIOS(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function canShareFiles(): boolean {
  const nav = navigator as Navigator & {
    canShare?: (d: ShareData) => boolean;
  };
  return typeof nav.canShare === "function";
}

/** Imperative handle for saving the QR; used by a page's own "Save QR to Gallery" button when
 *  `tappable` is false. */
export interface QRHandle {
  save: () => Promise<void>;
}

/** PayNow-styled QR: a purple card with the code in a white square and the white wordmark below.
 *  Download saves the whole card, flattened. */
export const QR = forwardRef<
  QRHandle,
  {
    payload: string;
    downloadName?: string;
    /**
     * True (default) renders the card as a button: tapping it saves the image, then fires `onTap`
     * (the dashboard preview). False renders a plain card, and saving is only reachable via the
     * ref's `save()` (the payer page's "Save QR to Gallery" button).
     */
    tappable?: boolean;
    /** Fired after a tap, once the save attempt (if any) has settled, e.g. to open a "pick your
     *  banking app" dialog. Only relevant when `tappable`. */
    onTap?: () => void;
  }
>(function QR({ payload, downloadName, tappable = true, onTap }, ref) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    QRCode.toDataURL(payload, QR_OPTS)
      .then((url) => live && setDataUrl(url))
      .catch((e) => live && setError((e as Error).message));
    renderCard(payload)
      .then((c) => live && setCanvas(c))
      .catch(() => {
        /* saving just won't work; on-screen QR still renders */
      });
    return () => {
      live = false;
    };
  }, [payload]);

  async function save() {
    if (canvas && downloadName) {
      if (isIOS() && canShareFiles()) {
        const nav = navigator as Navigator & {
          share?: (d: ShareData) => Promise<void>;
          canShare?: (d: ShareData) => boolean;
        };
        try {
          const blob: Blob = await new Promise((resolve, reject) =>
            canvas.toBlob(
              (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
              "image/png",
            ),
          );
          const file = new File([blob], downloadName, { type: "image/png" });
          if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
            await nav.share({ files: [file] });
          }
        } catch {
          /* user cancelled the share sheet — not an error */
        }
      } else {
        // Android / desktop: a plain download already lands the image where Gallery/Photos picks it up.
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = downloadName;
        a.click();
      }
    }
  }

  useImperativeHandle(ref, () => ({ save }), [canvas, downloadName]);

  async function handleTap() {
    await save();
    onTap?.();
  }

  if (error) return <div className="error-box">Could not render QR: {error}</div>;
  if (!dataUrl) return <div className="qr-frame">Rendering…</div>;

  const card = (
    <>
      <div className="paynow-qr__frame">
        <img src={dataUrl} alt="PayNow QR code" />
      </div>
      <img className="paynow-qr__logo" src={paynowLogo} alt="PayNow" />
    </>
  );

  if (!tappable) {
    return <div className="paynow-qr paynow-qr--static">{card}</div>;
  }

  return (
    <button
      type="button"
      className="paynow-qr"
      onClick={handleTap}
      aria-label="Tap to open your banking app"
    >
      {card}
    </button>
  );
});

/** The flattened branded card as a PNG Blob, for navigator.share(). */
export async function payloadToPngBlob(payload: string): Promise<Blob> {
  const canvas = await renderCard(payload);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/png");
  });
}
